'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { Tick, ArrowDown2, ArrowUp2 } from '@/components/icons';
import { controlVariants } from '@/components/ui/control-variants';
import { cn } from '@/lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            controlVariants(),
            'items-center justify-between whitespace-nowrap focus:ring-2 focus:ring-ring/20 data-[placeholder]:text-muted-foreground [&>span]:line-clamp-1',
            className,
        )}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ArrowDown2 className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

// As setas de rolagem são nossas, e não as do Radix, por dois motivos:
//
// 1. o auto-scroll do Radix salta um item inteiro a cada 50ms (~20 itens/s), o
//    que torna qualquer lista longa (as 27 UFs do perfil) impossível de mirar;
// 2. ao montar, a seta do Radix chama `scrollIntoView` no item em foco — quem
//    estivesse no topo da lista com um item selecionado lá embaixo era jogado
//    de volta para ele assim que a seta oposta aparecia, e a rolagem "travava".
//
// Aqui a rolagem é contínua, começa devagar e só acelera enquanto o ponteiro
// continuar sobre a seta.
const AUTO_SCROLL_START_SPEED = 110; // px/s
const AUTO_SCROLL_MAX_SPEED = 520; // px/s
const AUTO_SCROLL_RAMP_MS = 1200;

type ScrollDirection = 1 | -1;

/** Mostra a seta só quando há para onde rolar naquele sentido. */
function useCanScroll(viewportRef: React.RefObject<HTMLDivElement>, direction: ScrollDirection) {
    const [canScroll, setCanScroll] = React.useState(false);

    React.useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const update = () => {
            const max = viewport.scrollHeight - viewport.clientHeight;
            setCanScroll(
                direction === -1 ? viewport.scrollTop > 0 : Math.ceil(viewport.scrollTop) < max,
            );
        };

        update();
        viewport.addEventListener('scroll', update);
        // A área visível encolhe/cresce quando a seta oposta aparece ou some.
        const observer = new ResizeObserver(update);
        observer.observe(viewport);
        return () => {
            viewport.removeEventListener('scroll', update);
            observer.disconnect();
        };
    }, [viewportRef, direction]);

    return canScroll;
}

function useSmoothAutoScroll(
    viewportRef: React.RefObject<HTMLDivElement>,
    direction: ScrollDirection,
) {
    const frameRef = React.useRef<number | null>(null);
    const cleanupRef = React.useRef<(() => void) | null>(null);

    const stop = React.useCallback(() => {
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
        cleanupRef.current?.();
        cleanupRef.current = null;
    }, []);

    const start = React.useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const viewport = viewportRef.current;
            if (!viewport || frameRef.current !== null) return;

            // Quem encerra a rolagem é o movimento do ponteiro, e não um
            // `pointerleave` na seta: enquanto o popup anima na abertura a caixa
            // desliza por baixo de um cursor parado, e o `pointerleave` que isso
            // gera matava a rolagem no primeiro pixel.
            const button = event.currentTarget;
            const trackPointer = (move: PointerEvent) => {
                if (!button.contains(move.target as Node)) stop();
            };
            document.addEventListener('pointermove', trackPointer);
            cleanupRef.current = () => document.removeEventListener('pointermove', trackPointer);

            let previous: number | null = null;
            let elapsed = 0;
            // O Chrome arredonda `scrollTop` para pixels inteiros, então o resto de
            // cada quadro é acumulado aqui — sem isso a velocidade real vira "1px por
            // quadro", independente da que foi pedida.
            let pending = 0;
            const step = (now: number) => {
                const delta = previous === null ? 0 : now - previous;
                previous = now;
                elapsed += delta;
                const ramp = Math.min(elapsed / AUTO_SCROLL_RAMP_MS, 1);
                const speed =
                    AUTO_SCROLL_START_SPEED +
                    (AUTO_SCROLL_MAX_SPEED - AUTO_SCROLL_START_SPEED) * ramp;
                pending += (speed * delta) / 1000;
                const whole = Math.trunc(pending);
                if (whole > 0) {
                    pending -= whole;
                    viewport.scrollTop += direction * whole;
                }
                frameRef.current = requestAnimationFrame(step);
            };
            frameRef.current = requestAnimationFrame(step);
        },
        [viewportRef, direction, stop],
    );

    // A seta some ao chegar no fim da lista: sem isto o quadro seguinte
    // continuaria agendado depois do unmount.
    React.useEffect(() => stop, [stop]);

    return { start, stop };
}

const SelectScrollButton = ({
    viewportRef,
    direction,
    className,
}: {
    viewportRef: React.RefObject<HTMLDivElement>;
    direction: ScrollDirection;
    className?: string;
}) => {
    const canScroll = useCanScroll(viewportRef, direction);
    const { start, stop } = useSmoothAutoScroll(viewportRef, direction);

    if (!canScroll) return null;

    return (
        <div
            aria-hidden
            // Sobrepostas à lista, e não empilhadas nela: se a seta ocupasse
            // espaço no fluxo, o popup mudaria de altura toda vez que a seta
            // oposta aparecesse — o floating-ui reposicionava a caixa, a seta
            // escapava de debaixo do ponteiro e a rolagem morria ali.
            className={cn(
                'absolute inset-x-0 z-10 flex cursor-default items-center justify-center bg-popover py-1',
                direction === -1 ? 'top-0 rounded-t-md' : 'bottom-0 rounded-b-md',
                className,
            )}
            onPointerDown={start}
            onPointerMove={start}
            onPointerUp={stop}
            onPointerCancel={stop}
        >
            {direction === -1 ? <ArrowUp2 className="size-4" /> : <ArrowDown2 className="size-4" />}
        </div>
    );
};

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => {
    const viewportRef = React.useRef<HTMLDivElement>(null);

    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                ref={ref}
                className={cn(
                    'relative z-50 flex max-h-96 min-w-[8rem] flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    position === 'popper' &&
                        'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                    className,
                )}
                position={position}
                {...props}
            >
                <SelectScrollButton viewportRef={viewportRef} direction={-1} />
                <SelectPrimitive.Viewport
                    ref={viewportRef}
                    className={cn(
                        'p-1',
                        position === 'popper' &&
                            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollButton viewportRef={viewportRef} direction={1} />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cn('px-2 py-1.5 text-sm font-semibold', className)}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cn(
            'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            className,
        )}
        {...props}
    >
        <span className="absolute right-2 flex size-3.5 items-center justify-center">
            <SelectPrimitive.ItemIndicator>
                <Tick className="size-4" />
            </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cn('-mx-1 my-1 h-px bg-muted', className)}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectGroup,
    SelectValue,
    SelectTrigger,
    SelectContent,
    SelectLabel,
    SelectItem,
    SelectSeparator,
};
